import { supabase } from './supabase';

const MODULE_KEY = 'cs';
const SCREENSHOT_BUCKET = 'bug-screenshots';
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;

export type BugSeverity = 'blocked' | 'annoying' | 'weird' | 'suggestion';

export interface BugReportInput {
  what_happened: string;
  what_trying_to_do: string;
  severity: BugSeverity;
  blocks_work: boolean;
  impact_description: string;
  screenshot: Blob | File | null;
  current_view: string | null;
  extra_context?: Record<string, unknown>;
}

export interface BugReportResult {
  ok: boolean;
  error?: string;
}

function extForBlob(blob: Blob): 'png' | 'jpg' | 'webp' {
  const t = (blob.type || '').toLowerCase();
  if (t.includes('jpeg') || t.includes('jpg')) return 'jpg';
  if (t.includes('webp')) return 'webp';
  return 'png';
}

async function uploadScreenshot(
  blob: Blob
): Promise<{ path: string | null; error: string | null }> {
  if (blob.size > MAX_SCREENSHOT_BYTES) {
    return { path: null, error: 'Screenshot exceeds 5MB limit' };
  }
  const ext = extForBlob(blob);
  const path = `${MODULE_KEY}/${crypto.randomUUID()}.${ext}`;
  const contentType =
    ext === 'jpg' ? 'image/jpeg' : ext === 'webp' ? 'image/webp' : 'image/png';

  const { error } = await supabase.storage
    .from(SCREENSHOT_BUCKET)
    .upload(path, blob, { contentType, upsert: false });

  if (error) {
    console.warn('[bugReporter] screenshot upload failed:', error.message);
    return { path: null, error: error.message };
  }
  return { path, error: null };
}

async function resolveReporter(): Promise<{
  id: string | null;
  email: string | null;
  name: string | null;
}> {
  try {
    const { data: authData } = await supabase.auth.getUser();
    const authUser = authData?.user;
    if (!authUser) return { id: null, email: null, name: null };

    const email = authUser.email ?? null;

    const { data: profile } = await supabase
      .from('tb_users')
      .select('id, full_name, display_name')
      .eq('auth_id', authUser.id)
      .maybeSingle();

    if (!profile) return { id: null, email, name: null };

    const name =
      (profile.display_name as string | null) ??
      (profile.full_name as string | null) ??
      null;
    return { id: profile.id as string, email, name };
  } catch (err) {
    console.warn('[bugReporter] reporter resolution failed:', err);
    return { id: null, email: null, name: null };
  }
}

export async function submitBugReport(
  input: BugReportInput
): Promise<BugReportResult> {
  try {
    const reporter = await resolveReporter();

    let screenshotPath: string | null = null;
    let screenshotUploadError: string | null = null;
    if (input.screenshot) {
      const result = await uploadScreenshot(input.screenshot);
      screenshotPath = result.path;
      screenshotUploadError = result.error;
    }

    const contextSnapshot: Record<string, unknown> = {
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      user_agent: navigator.userAgent,
      app_version:
        (import.meta.env.VITE_APP_COMMIT as string | undefined) ?? null,
      ...(input.extra_context ?? {}),
    };
    if (screenshotUploadError) {
      contextSnapshot.screenshot_upload_error = screenshotUploadError;
    }

    const payload = {
      module_key: MODULE_KEY,
      what_happened: input.what_happened,
      what_trying_to_do: input.what_trying_to_do,
      severity: input.severity,
      blocks_work: input.blocks_work,
      impact_description: input.blocks_work ? input.impact_description : '',
      reporter_id: reporter.id,
      reporter_email: reporter.email,
      reporter_name: reporter.name,
      current_route: window.location.pathname,
      current_view: input.current_view,
      screenshot_path: screenshotPath,
      context_snapshot: contextSnapshot,
    };

    const { error } = await supabase.from('br_bug_reports').insert(payload);
    if (error) {
      console.error('[bugReporter] insert failed:', error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unexpected error';
    console.error('[bugReporter] unexpected failure:', err);
    return { ok: false, error: msg };
  }
}
